/**
 * crypto — the Nexus Keychain client-side cryptography, the way the Bitwarden apps do it
 * (NEXUS-569, NEXUS-562). Moved here from packages/admin-ui/src/keychain/vaultCrypto.ts so the
 * admin console and app SPAs (Vault Web first) share one implementation.
 *
 * Everything secret stays on the device: the master password, the master key and the user key never
 * leave the page. The server receives only a hash of the master key and strings that are already
 * encrypted. Uses WebCrypto (also available in Node 20+, so the same code is tested against the
 * server and the official Bitwarden CLI) and hash-wasm for Argon2id.
 *
 * Formats (Bitwarden):
 *   master key   = Argon2id(password, salt = SHA-256(email), m, t, p) or PBKDF2-SHA256(password, email, n)
 *   login hash   = base64(PBKDF2-SHA256(master key, password, 1))
 *   stretched    = HKDF-Expand(master key, "enc") ‖ HKDF-Expand(master key, "mac")
 *   user key     = 64 random bytes: enc = first 32, mac = last 32
 *   EncString 2  = "2." + iv + "|" + AES-256-CBC ciphertext + "|" + HMAC-SHA256(iv ‖ ct)   (base64 parts)
 */
import { KeychainError } from './errors.js';
export const KDF_PBKDF2 = 0;
export const KDF_ARGON2ID = 1;
/** Defaults for new vaults: Argon2id, 64 MiB, 3 passes, 4 lanes (Bitwarden's recommended setting). */
export const DEFAULT_KDF = { kdf: KDF_ARGON2ID, kdfIterations: 3, kdfMemory: 64, kdfParallelism: 4 };
const subtle = () => {
    const s = globalThis.crypto?.subtle;
    if (!s)
        throw new KeychainError('format', 'WebCrypto is not available in this environment (use https).');
    return s;
};
export const utf8 = (s) => new TextEncoder().encode(s);
const utf8Decode = (b) => new TextDecoder('utf-8', { fatal: true }).decode(b);
export const toB64 = (b) => {
    const u = b instanceof Uint8Array ? b : new Uint8Array(b);
    let s = '';
    for (let i = 0; i < u.length; i += 0x8000)
        s += String.fromCharCode(...u.subarray(i, i + 0x8000));
    return btoa(s);
};
export const fromB64 = (s) => {
    let bin;
    try {
        bin = atob(s);
    }
    catch {
        throw new KeychainError('format', 'Not valid base64.');
    }
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++)
        out[i] = bin.charCodeAt(i);
    return out;
};
const concat = (...parts) => {
    const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
    let o = 0;
    for (const p of parts) {
        out.set(p, o);
        o += p.length;
    }
    return out;
};
const buf = (u) => u.buffer.slice(u.byteOffset, u.byteOffset + u.byteLength);
/** Constant-time comparison of two byte strings. */
function equalBytes(a, b) {
    if (a.length !== b.length)
        return false;
    let d = 0;
    for (let i = 0; i < a.length; i++)
        d |= a[i] ^ b[i];
    return d === 0;
}
export const normalizeEmail = (email) => email.trim().toLowerCase();
async function hmac(key, data) {
    const k = await subtle().importKey('raw', buf(key), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
    return new Uint8Array(await subtle().sign('HMAC', k, buf(data)));
}
async function pbkdf2(password, salt, iterations) {
    const k = await subtle().importKey('raw', buf(password), 'PBKDF2', false, ['deriveBits']);
    return new Uint8Array(await subtle().deriveBits({ name: 'PBKDF2', salt: buf(salt), iterations, hash: 'SHA-256' }, k, 256));
}
/** The master key. Argon2id with 64 MiB takes one to three seconds in a browser. */
export async function deriveMasterKey(password, email, kdf) {
    const salt = utf8(normalizeEmail(email));
    if (kdf.kdf === KDF_PBKDF2)
        return pbkdf2(utf8(password), salt, kdf.kdfIterations);
    if (kdf.kdf !== KDF_ARGON2ID)
        throw new KeychainError('format', `Unknown KDF ${kdf.kdf}.`);
    const { argon2id } = await import('hash-wasm');
    const saltHash = new Uint8Array(await subtle().digest('SHA-256', buf(salt)));
    return argon2id({
        password: utf8(password), salt: saltHash, iterations: kdf.kdfIterations,
        memorySize: (kdf.kdfMemory ?? 64) * 1024, parallelism: kdf.kdfParallelism ?? 4, hashLength: 32, outputType: 'binary',
    });
}
/** What the server stores (after re-hashing) to check the master password at login. */
export async function masterPasswordHash(masterKey, password) {
    return toB64(await pbkdf2(masterKey, utf8(password), 1));
}
/** HKDF-Expand the 32-byte master key into the 64-byte key that protects the user key. */
export async function stretch(masterKey) {
    return { enc: await hmac(masterKey, concat(utf8('enc'), new Uint8Array([1]))), mac: await hmac(masterKey, concat(utf8('mac'), new Uint8Array([1]))) };
}
/** Split 64 raw key bytes into the enc/mac halves. Throws on any other length. */
export function splitKey(raw) {
    if (raw.length !== 64)
        throw new KeychainError('format', `A symmetric key must be 64 bytes, got ${raw.length}.`);
    return { enc: raw.slice(0, 32), mac: raw.slice(32) };
}
/** Overwrite key material. Call when a key is no longer needed. */
export function zeroKey(key) {
    if (!key)
        return;
    if (key instanceof Uint8Array) {
        key.fill(0);
        return;
    }
    key.enc.fill(0);
    key.mac.fill(0);
}
/** New random 64-byte key (a user key or a per-item key). */
export function randomKey() {
    return globalThis.crypto.getRandomValues(new Uint8Array(64));
}
/** EncString type 2 over raw bytes. */
export async function encrypt(plain, key) {
    const iv = globalThis.crypto.getRandomValues(new Uint8Array(16));
    const k = await subtle().importKey('raw', buf(key.enc), { name: 'AES-CBC' }, false, ['encrypt']);
    const ct = new Uint8Array(await subtle().encrypt({ name: 'AES-CBC', iv }, k, buf(plain)));
    const mac = await hmac(key.mac, concat(iv, ct));
    return `2.${toB64(iv)}|${toB64(ct)}|${toB64(mac)}`;
}
const ENC_RE = /^([0-9])\.([A-Za-z0-9+/=]+)(?:\|([A-Za-z0-9+/=]+))?(?:\|([A-Za-z0-9+/=]+))?$/;
/** True when the string has the EncString shape the server accepts (we check shape, not content). */
export function isEncString(v) {
    return typeof v === 'string' && ENC_RE.test(v);
}
/** Decrypt an EncString type 2 to raw bytes. Checks the MAC before touching the ciphertext. */
export async function decrypt(enc, key) {
    const m = ENC_RE.exec(enc);
    if (!m)
        throw new KeychainError('format', 'Not an encrypted string.');
    const type = Number(m[1]);
    if (type !== 2)
        throw new KeychainError('format', `Encrypted string type ${type} is not supported (only AES-256-CBC with HMAC-SHA256).`);
    if (!m[3] || !m[4])
        throw new KeychainError('format', 'Encrypted string is missing its ciphertext or MAC.');
    const iv = fromB64(m[2]);
    const ct = fromB64(m[3]);
    const mac = fromB64(m[4]);
    if (iv.length !== 16)
        throw new KeychainError('format', 'Encrypted string has a bad IV.');
    const want = await hmac(key.mac, concat(iv, ct));
    if (!equalBytes(want, mac))
        throw new KeychainError('format', 'Encrypted string failed its integrity check (wrong key or tampered data).');
    const k = await subtle().importKey('raw', buf(key.enc), { name: 'AES-CBC' }, false, ['decrypt']);
    try {
        return new Uint8Array(await subtle().decrypt({ name: 'AES-CBC', iv: buf(iv) }, k, buf(ct)));
    }
    catch {
        throw new KeychainError('format', 'Encrypted string could not be decrypted.');
    }
}
/** Encrypt a UTF-8 string. `null`/`undefined`/empty stay `null` (the clients send absent fields as null). */
export async function encryptString(plain, key) {
    if (plain == null || plain === '')
        return null;
    return encrypt(utf8(plain), key);
}
/** Decrypt to a UTF-8 string. `null` stays `null`. */
export async function decryptString(enc, key) {
    if (enc == null || enc === '')
        return null;
    try {
        return utf8Decode(await decrypt(enc, key));
    }
    catch (e) {
        if (e instanceof KeychainError)
            throw e;
        throw new KeychainError('format', 'Decrypted value is not valid text.');
    }
}
/**
 * Build everything a new vault needs. Generates a random 512-bit user key and an RSA-2048 key pair
 * (for sharing later), encrypts the user key with the stretched master key and the private key with
 * the user key. Returns only values that are safe to send.
 */
export async function createVaultRequest(input) {
    const kdf = input.kdf ?? DEFAULT_KDF;
    const email = normalizeEmail(input.email);
    const masterKey = await deriveMasterKey(input.password, email, kdf);
    const hash = await masterPasswordHash(masterKey, input.password);
    const userKeyRaw = randomKey();
    const userKey = splitKey(userKeyRaw);
    const pair = await subtle().generateKey({ name: 'RSA-OAEP', modulusLength: 2048, publicExponent: new Uint8Array([1, 0, 1]), hash: 'SHA-1' }, true, ['encrypt', 'decrypt']);
    const spki = new Uint8Array(await subtle().exportKey('spki', pair.publicKey));
    const pkcs8 = new Uint8Array(await subtle().exportKey('pkcs8', pair.privateKey));
    const stretched = await stretch(masterKey);
    const req = {
        email, name: input.name?.trim() || null, masterPasswordHash: hash, masterPasswordHint: input.hint?.trim() || null,
        key: await encrypt(userKeyRaw, stretched),
        keys: { publicKey: toB64(spki), encryptedPrivateKey: await encrypt(pkcs8, userKey) },
        ...kdf,
    };
    zeroKey(masterKey);
    zeroKey(stretched);
    zeroKey(userKey);
    zeroKey(userKeyRaw);
    pkcs8.fill(0);
    return req;
}
/** Simple checks before deriving anything. Returns a message, or null when the password is acceptable. */
export function checkMasterPassword(password, confirm, hint, email) {
    if (password.length < 12)
        return 'Use at least 12 characters. A few random words work well.';
    if (password.length > 512)
        return 'That password is too long.';
    if (password !== confirm)
        return 'The two passwords do not match.';
    const lower = password.toLowerCase();
    const local = normalizeEmail(email).split('@')[0] ?? '';
    if (lower.includes(normalizeEmail(email)) || (local.length >= 4 && lower.includes(local)))
        return 'Do not use your email address in the password.';
    if (hint && hint.trim() && lower.includes(hint.trim().toLowerCase()))
        return 'The hint must not contain the password.';
    if (/^(.)\1+$/.test(password))
        return 'Do not repeat one character.';
    return null;
}
//# sourceMappingURL=crypto.js.map